import { formatFileSize } from "@impero-com/common/utils/formatting";
import { t } from "@impero-com/common/utils/i18n";
import { AxiosProgressEvent } from "axios";
import uuid from "uuid";

import {
    UploaderFileErrored,
    UploaderFileSuccess,
    UploaderFileUploading,
    UploaderFile,
    FileExtensionWhiteList,
} from "../types";
import {
    FILE_SIZE_LIMIT,
    MAX_FILES,
    formatBlobUrl,
    mapCommonFields,
    matchesWhitelist,
    validFileSize,
} from "../utils";

import { useUploadBlobsMutation } from "./useUploadBlobsMutation";

const chunkArray = <T>(array: T[], chunkSize: number): Array<T[]> =>
    array.reduce<Array<T[]>>(
        (acc, item, index) =>
            index % chunkSize === 0
                ? [...acc, [item]]
                : [...acc.slice(0, -1), [...acc[acc.length - 1], item]],
        []
    );

interface UploadHandlerFile extends UploaderFileUploading {
    index: number; // We expect the order of upload and uploaded files to always match - used for tracking progress
    file: File;
}

const INITIAL_UPLOAD_PERCENT = 10;
export const useFileUploadHandler = (
    fileExtensionWhitelist: FileExtensionWhiteList,
    setFiles: (prevFiles: (prev: UploaderFile[]) => UploaderFile[]) => void,
    preUploadCallback?: () => Promise<boolean>
) => {
    const { mutateAsync: uploadBlobs } = useUploadBlobsMutation();

    const updateFiles = (updatedFiles: UploaderFile[]) =>
        setFiles((prevValue) =>
            prevValue.map(
                (file) =>
                    updatedFiles.find(
                        (updatedFile) => updatedFile.keyId === file.keyId
                    ) ?? file
            )
        );

    const handleUpload = async (handlerFile: UploadHandlerFile[]) => {
        const handleFailedUpload = (
            failedFiles: UploadHandlerFile[],
            errMsg: string
        ) => {
            const updatedFiles: UploaderFileErrored[] = failedFiles.map(
                (file) => ({
                    status: "error",
                    keyId: file.keyId,
                    error: errMsg,
                    ...mapCommonFields(file.file),
                })
            );
            updateFiles(updatedFiles);
        };

        const handleUploadProgress = (
            e: AxiosProgressEvent,
            uploadingFiles: UploadHandlerFile[]
        ) => {
            const { loaded, total } = e;
            const toLoad = total ?? 1;
            const percent = (loaded / toLoad) * 100;
            const updatedFiles: UploaderFileUploading[] = uploadingFiles.map(
                (file) => ({
                    keyId: file.keyId,
                    status: "uploading",
                    percent,
                    ...mapCommonFields(file.file),
                })
            );
            updateFiles(updatedFiles);
        };

        try {
            const res = await uploadBlobs({
                blobs: handlerFile.map((file) => file.file),
                onUploadProgress: (e) => handleUploadProgress(e, handlerFile),
            });

            if (res.status === 200) {
                const uploadedFiles = res.data.content;
                const updatedFiles: UploaderFileSuccess[] = uploadedFiles.map(
                    (file, index) => ({
                        ...file.resource,
                        keyId: handlerFile[index].keyId,
                        url: formatBlobUrl(file.resource.id),
                        status: "success",
                    })
                );
                updateFiles(updatedFiles);
            } else {
                handleFailedUpload(handlerFile, t("File_Upload_Failed"));
            }
        } catch {
            handleFailedUpload(handlerFile, t("File_Upload_Failed"));
        }
    };

    const getFileError = (file: File) => {
        // Order kind of matters by significance of error
        if (!matchesWhitelist(fileExtensionWhitelist, file.name)) {
            return t("File_Upload_Blocked");
        } else if (!validFileSize(file.size)) {
            return t("File_Upload_Oversize", {
                size: formatFileSize(FILE_SIZE_LIMIT),
            });
        }
        return null;
    };

    return (selectedFiles: File[]) => {
        const upload = async (granted: boolean) => {
            if (!granted) {
                console.debug(
                    "Upload was not granted by preUploadCallback. Cancelling silently!"
                );
                return;
            }

            const erroredFiles: UploaderFileErrored[] = selectedFiles.reduce<
                UploaderFileErrored[]
            >((acc, file) => {
                const error = getFileError(file);
                if (!error) {
                    return acc;
                }
                const erroredFile: UploaderFileErrored = {
                    keyId: uuid.v4(),
                    status: "error",
                    error,
                    ...mapCommonFields(file),
                };
                return [...acc, erroredFile];
            }, []);

            const uploadFiles: UploadHandlerFile[] = selectedFiles
                .filter((file) => !getFileError(file))
                .map((file, index) => ({
                    keyId: uuid.v4(),
                    status: "uploading",
                    percent: INITIAL_UPLOAD_PERCENT,
                    file,
                    index,
                    ...mapCommonFields(file),
                }));

            setFiles((prevValue) => [
                ...prevValue,
                ...erroredFiles,
                ...uploadFiles,
            ]);

            if (uploadFiles.length === 0) {
                return;
            }

            const uploadChunks = chunkArray(uploadFiles, MAX_FILES);
            for (const chunk of uploadChunks) {
                await handleUpload(chunk);
            }
        };

        if (preUploadCallback) {
            preUploadCallback().then(upload);
        } else {
            upload(true);
        }
    };
};
